// Écran du jour chez elle : « Prise » est le seul gros bouton (parcours, étape 5).
// Annulation 10 min après (erreur de doigt), information des jours d'arrêt,
// texte de la notice quand le délai d'oubli est passé.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Bouton, Chargement, Erreur, ErreurReseau, Texte, Titre } from '../components/base';
import { TexteOubli } from '../components/TexteOubli';
import { api, type JourComplet } from '../lib/api';
import { dateLongue, heure } from '../lib/format';
import { noterPerteProcheVue, perteProcheVue } from '../lib/jeton';
import { enRetard } from '../lib/jour';
import { CLES, useAujourdhui, useRegles } from '../lib/requetes';

function useMaintenant(intervalleMs = 15_000): Date {
  const [maintenant, setMaintenant] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setMaintenant(new Date()), intervalleMs);
    return () => clearInterval(id);
  }, [intervalleMs]);
  return maintenant;
}

const TITRE = {
  actif: 'jour.comprime',
  placebo: 'jour.placebo',
  arret: 'jour.arretTitre',
  'avant-debut': 'jour.avantDebut',
} as const;

/** L'écran dit toujours de quel comprimé il s'agit : celui d'hier ou celui de ce soir (#26). */
function Entete({ j, fuseau }: Readonly<{ j: JourComplet; fuseau: string }>) {
  const { t } = useTranslation();
  const { genre, rang } = j.jour;
  const hier = j.cible === 'hier';
  const date = dateLongue(j.date);
  return (
    <>
      <p className="text-secondaire text-sm">
        {hier ? t('jour.soireeHier', { date }) : t('jour.soiree', { date })}
      </p>
      <Titre>
        {hier && genre === 'actif' ? t('jour.hierTitre', { rang }) : t(TITRE[genre], { rang })}
      </Titre>
      {hier && !j.prise && <Texte>{t('jour.hierTexte')}</Texte>}
      {/* Pas de « Rappel à » un jour d'arrêt : aucun rappel ne part (#14). */}
      {!(hier && !j.prise) && genre !== 'arret' && (
        <Texte discret>{t('jour.rappelA', { heure: heure(j.heure, fuseau) })}</Texte>
      )}
    </>
  );
}

/** Le comprimé d'hier vient d'être noté ; l'écran est passé à ce soir (#26). */
function Rattrape({ j, fuseau }: Readonly<{ j: JourComplet; fuseau: string }>) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const maintenant = useMaintenant(5_000);
  const r = j.rattrape;
  const annuler = useMutation({
    mutationFn: (date: string) => api.annuler(date),
    onSuccess: () => client.invalidateQueries({ queryKey: CLES.aujourdhui }),
  });
  if (!r || maintenant >= r.annulableJusqua) return null;
  return (
    <div className="border-filet mb-8 border-b pb-6">
      <p role="status" className="font-semibold">
        {t('jour.hierNote', { heure: heure(r.le, fuseau) })}
      </p>
      <Bouton
        variante="secondaire"
        className="mt-3"
        disabled={annuler.isPending}
        onClick={() => annuler.mutate(r.date)}
      >
        {t('jour.annuler', { heure: heure(r.annulableJusqua, fuseau) })}
      </Bouton>
      {annuler.isError && <Erreur>{t('erreur.annulation')}</Erreur>}
    </div>
  );
}

function Prise({ j, fuseau }: Readonly<{ j: JourComplet; fuseau: string }>) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const maintenant = useMaintenant(5_000);
  const rafraichir = () => client.invalidateQueries({ queryKey: CLES.aujourdhui });
  const prendre = useMutation({
    mutationFn: api.prendre,
    onSuccess: (r) => client.setQueryData(CLES.aujourdhui, r),
  });
  const annuler = useMutation({ mutationFn: () => api.annuler(j.date), onSuccess: rafraichir });

  if (!j.prise) {
    return (
      <div className="mt-8">
        <Bouton grand disabled={prendre.isPending} onClick={() => prendre.mutate()}>
          {t('jour.prise')}
        </Bouton>
        {maintenant < j.heure ? (
          <Texte discret>{t('jour.priseAvance')}</Texte>
        ) : (
          j.cible !== 'hier' && <Texte>{t('jour.pasEncoreNote')}</Texte>
        )}
        {prendre.isError && <Erreur>{t('erreur.action')}</Erreur>}
      </div>
    );
  }
  const annulable = maintenant < j.prise.annulableJusqua;
  return (
    <div className="mt-8">
      <p role="status" className="text-xl font-semibold">
        {t('jour.priseLe', { heure: heure(j.prise.le, fuseau) })}
      </p>
      {annulable && (
        <Bouton
          variante="secondaire"
          className="mt-4"
          disabled={annuler.isPending}
          onClick={() => annuler.mutate()}
        >
          {t('jour.annuler', { heure: heure(j.prise.annulableJusqua, fuseau) })}
        </Bouton>
      )}
      {annuler.isError && <Erreur>{t('erreur.annulation')}</Erreur>}
    </div>
  );
}

function Arret({ j }: Readonly<{ j: JourComplet }>) {
  const { t } = useTranslation();
  const { repriseDans, reprise } = j.jour;
  return (
    <Texte>
      {repriseDans === 1
        ? t('jour.repriseDemain')
        : t('jour.repriseDans', { n: repriseDans, jour: reprise ? dateLongue(reprise) : '' })}
    </Texte>
  );
}

function Oubli({ j }: Readonly<{ j: JourComplet }>) {
  const { data: regles } = useRegles();
  if (!j.regle) return null;
  const renvoi = j.regle.delaiH !== null && regles ? regles.renvoi : '';
  return <TexteOubli regle={j.regle} renvoi={renvoi} />;
}

/** Le proche ne reçoit plus les alertes (#41) : dit une fois par perte, jusqu'à « Compris ». */
function ProcheSansAlertes({ perduLe }: Readonly<{ perduLe: Date | null }>) {
  const { t } = useTranslation();
  const [vue, setVue] = useState(() => perduLe === null || perteProcheVue(perduLe));
  if (perduLe === null || vue) return null;
  return (
    <div role="alert" className="border-filet mb-8 border-b pb-6">
      <p className="text-alerte font-medium">{t('jour.procheSansAlertes')}</p>
      <Bouton
        variante="secondaire"
        className="mt-3"
        onClick={() => {
          noterPerteProcheVue(perduLe);
          setVue(true);
        }}
      >
        {t('jour.compris')}
      </Bouton>
    </div>
  );
}

export function AujourdhuiElle({
  fuseau,
  procheAbonnementPerduLe = null,
}: Readonly<{ fuseau: string; procheAbonnementPerduLe?: Date | null }>) {
  const { t } = useTranslation();
  const { data, isError, refetch } = useAujourdhui();
  const maintenant = useMaintenant();
  if (isError) return <ErreurReseau onReessayer={() => void refetch()} />;
  if (!data) return <Chargement />;
  if (!data.reglagesComplets) return <Texte>{t('jour.reglagesAFaire')}</Texte>;
  if (data.jour.genre === 'avant-debut') {
    return <Titre>{t('jour.avantDebut', { date: dateLongue(data.jour.debut ?? '') })}</Titre>;
  }
  return (
    <section>
      <ProcheSansAlertes
        key={procheAbonnementPerduLe?.toISOString()}
        perduLe={procheAbonnementPerduLe}
      />
      <Rattrape j={data} fuseau={fuseau} />
      <Entete j={data} fuseau={fuseau} />
      {data.jour.genre === 'arret' ? <Arret j={data} /> : <Prise j={data} fuseau={fuseau} />}
      {enRetard(data, maintenant) && <Oubli j={data} />}
      <p className="mt-10 text-sm">
        <Link to="/oubli" className="text-accent underline underline-offset-4">
          {t('jour.lienOubli')}
        </Link>
      </p>
    </section>
  );
}
